import { useState } from "react";
import { Link, type LinkProps } from "react-router-dom";
import {
  ISSUE_LABEL,
  RANGE_LABEL,
  type EntityRef,
  type IssueReason,
  type OwnerRef,
  type RangeKey,
  type ResourceUsage,
  type Section,
  type TrendPanel,
} from "@k8s-dashboard/contracts";
import { LineChart } from "@/components/LineChart";
import { Panel } from "@/components/primitives";
import { num, unitSuffix } from "@/lib/format";
import { routeCrumb } from "@/app/routeRegistry";
import { useApplyNamespaceSelection } from "@/state/namespaceSelectionIdentity";
import { isNamespaceName } from "@/state/namespaceSelection";
import { useT } from "@/i18n/I18nProvider";

/* ── Breadcrumb ──────────────────────────────────────────────────────────── */

/** 드릴다운 경로를 항상 노출합니다. 어디까지 내려왔는지 모르면 되돌아가지 못합니다. */
export function Breadcrumb({ items }: { items: Array<{ label: string; to?: string }> }) {
  return (
    <nav className="crumbs" aria-label="현재 위치">
      {items.map((it, i) => (
        <span key={`${it.label}-${i}`} className="crumbs__item">
          {it.to ? <Link to={it.to}>{it.label}</Link> : <span aria-current="page">{it.label}</span>}
          {i < items.length - 1 && (
            <span className="crumbs__sep" aria-hidden="true">
              /
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

/* ── 링크 경로 ───────────────────────────────────────────────────────────── */

/** Scope·시간 범위를 유지한 채 이동합니다. (이슈 #15 작업 범위: URL에 상태 유지) */
export function withSearch(path: string, search: string, extra: Record<string, string> = {}) {
  const p = new URLSearchParams(search);
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  return `${path}?${p}`;
}

/**
 * 라우트 하나를 breadcrumb 항목으로 만듭니다. (ADR 0023)
 *
 * 라벨을 화면마다 손으로 적으면 nav만 바뀌고 breadcrumb은 옛 이름으로 남습니다.
 * nav·팔레트와 **같은 레지스트리**에서 읽으면 그 어긋남 자체가 생기지 않습니다.
 * Scope·시간 범위는 기존대로 질의에 실어 유지합니다.
 */
export function routeCrumbAt(id: string, path: string, search: string): { label: string; to?: string } {
  return routeCrumb(id, withSearch(path, search));
}

/** 현재 화면 칸. 링크가 아니므로 라벨만 레지스트리에서 빌립니다. */
export function currentRouteCrumb(id: string): { label: string; to?: string } {
  return routeCrumb(id);
}

/**
 * 드릴다운 breadcrumb 항목을 만듭니다.
 *
 * 다섯 화면이 "Cluster Overview → Namespaces → …" 앞 두 칸을 각자 적고 있었습니다.
 * 앞 칸이 바뀌면 다섯 곳을 함께 고쳐야 하고, 한 곳을 잊으면 그 화면만 다른 곳으로
 * 되돌아갑니다. 앞 두 칸은 여기서 만들고, 화면은 자기 뒤쪽만 넘깁니다.
 * 라벨은 라우트 레지스트리가 유일한 원천입니다 (ADR 0023).
 */
export function drillCrumbs(
  search: string,
  namespace: string | undefined,
  ...tail: Array<{ label: string; to?: string }>
): Array<{ label: string; to?: string }> {
  return [
    routeCrumbAt("overview", "/", search),
    routeCrumbAt("namespaces", "/namespaces", search),
    ...(namespace ? [{ label: namespace, to: refPath({ namespace }, search) }] : []),
    ...tail,
  ];
}

/** 링크 대상. EntityRef 전체가 아니라 **경로를 정하는 부분**만 받습니다 —
 * 목록 행처럼 clusterId를 들고 있지 않은 호출자도 같은 규칙을 쓰게 됩니다. */
export type LinkRef = Partial<Pick<EntityRef, "namespace" | "podName" | "podUid" | "workloadKind" | "workloadName" | "workloadUid">>;

/**
 * 리소스 상세 경로. namespace는 **경로 세그먼트**입니다. (#111)
 *
 * 쿼리(`?ns=`)로 싣던 것을 경로로 올렸습니다 — 전역 선택은 URL에 두지 않는다는
 * 규칙(#94)과 겉보기에 충돌하지 않으면서, 링크 하나로 같은 리소스가 열립니다.
 * namespace를 모르는 ref(토폴로지의 External 노드, Node 등)는 상세를 특정할 수 없으므로
 * 목록으로 보냅니다 — 빈 세그먼트로 404를 만들지 않습니다.
 *
 * `extra`는 화면이 더 싣고 싶은 질의입니다(Live Logs의 container·live 등).
 */
export function refPath(ref: LinkRef, rawSearch: string, extra: Record<string, string> = {}): string {
  /* 넘겨받은 질의에 남은 옛 `ns`는 버립니다. 경로가 namespace를 정하는데 쿼리에도
     남으면 AppShell이 그 값을 전역 선택으로 삼아, 링크 대상과 다른 곳을 가리킵니다. */
  const carried = new URLSearchParams(rawSearch);
  carried.delete("ns");
  /* **물려받은 `uid`도 버립니다.** (#208)
     `uid`는 이 링크가 가리키는 **대상의 신원**이고, 출처 화면의 신원이 아닙니다.
     Pod 상세(`?uid=pod-A`)에서 breadcrumb을 타고 Namespace 상세로 간 뒤 그 화면의
     Workload 링크를 누르면, 물려받은 pod-A가 Workload 요청의 `uid`로 실려 나갑니다.
     서버가 그 값을 실제로 검증하게 된 뒤부터는(요청 UID 신원 게이트) 그 링크가
     404가 됩니다. 신원은 **이 ref가 명시한 것**에서만 옵니다 — 아래에서 `ref.podUid`나
     호출부가 넘긴 `extra.uid`로 다시 채웁니다.
     이 규칙은 Live Logs의 "현재 Pod 다시 열기"도 고칩니다: 그 링크는 교체된 Pod의
     옛 UID를 버리고 **지금 그 이름의 Pod**를 열어야 합니다. */
  carried.delete("uid");
  const search = carried.toString();
  /* "—"는 namespace가 없다는 표시값입니다 — 이름이 아닙니다. */
  const ns = ref.namespace && ref.namespace !== "—" ? encodeURIComponent(ref.namespace) : "";
  if (ns && ref.podName) {
    return withSearch(`/namespaces/${ns}/pods/${encodeURIComponent(ref.podName)}`, search, {
      ...(ref.podUid ? { uid: ref.podUid } : {}),
      ...extra,
    });
  }
  if (ns && ref.workloadName && ref.workloadKind) {
    return withSearch(
      `/namespaces/${ns}/workloads/${ref.workloadKind}/${encodeURIComponent(ref.workloadName)}`,
      search,
      { ...(ref.workloadUid ? { uid: ref.workloadUid } : {}), ...extra },
    );
  }
  if (ns) return withSearch(`/namespaces/${ns}`, search, extra);
  return withSearch("/namespaces", search, extra);
}

/** Logs Explorer로 Pod와 **같은 시간 범위**를 넘깁니다. 전역 선택은 URL에 넣지 않습니다. */
export function logsPath(ref: LinkRef, search: string) {
  const carried = new URLSearchParams(search);
  carried.delete("ns");
  carried.delete("cluster");
  carried.delete("pod");
  carried.delete("podUid");
  carried.delete("uid");
  return withSearch("/logs", carried.toString(), {
    ...(ref.podName ? { pod: ref.podName } : {}),
    ...(ref.podUid ? { uid: ref.podUid } : {}),
  });
}

/** 서버 응답의 리소스 namespace를 검증된 전역 선택으로 먼저 커밋한 뒤 Logs로 이동합니다. */
export function LogsLink({ resourceRef, search, children, ...props }: Omit<LinkProps, "to"> & {
  resourceRef: LinkRef;
  search: string;
}) {
  const applyNamespaceSelection = useApplyNamespaceSelection();
  const { t } = useT("common");
  const [applyDenied, setApplyDenied] = useState(false);
  const to = logsPath(resourceRef, search);
  const namespace = resourceRef.namespace;
  if (!isNamespaceName(namespace)) {
    return (
      <>
        <span role="link" aria-disabled="true" className={props.className}>{children}</span>
        <span className="muted" role="status">{t("common.logsLink.namespaceUnavailable")}</span>
      </>
    );
  }
  return (
    <>
      <Link
        {...props}
        to={to}
        onClick={(event) => {
          props.onClick?.(event);
          if (event.defaultPrevented) return;
          /* 다른 browsing context는 이 탭의 Apply를 공유하지 않습니다. clean href의
             네이티브 Link 동작을 유지하고, 그 창의 기존 검증된 선택만 사용합니다. */
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
              (props.target && props.target !== "_self")) return;
          const selection = { mode: "selected", names: [namespace] } as const;
          if (!applyNamespaceSelection(selection)) {
            event.preventDefault();
            setApplyDenied(true);
            return;
          }
          setApplyDenied(false);
        }}
      >
        {children}
      </Link>
      {applyDenied && <span className="muted" role="status">{t("common.logsLink.applyDenied")}</span>}
    </>
  );
}

/* ── 추세 패널 그리드 ─────────────────────────────────────────────────── */

/**
 * 시계열 추세 패널 그리드.
 *
 * Overview·Namespace·Workload·Pod 네 화면이 **같은 마크업**을 각자 들고 있었습니다.
 * 같은 화면 요소가 네 벌이면 축 규칙(이중 y축 금지·계열 색 고정)을 한 곳만 고쳐도
 * 나머지 셋은 옛 규칙으로 남습니다. 그리기는 여기 하나로 모으고, 화면마다 다른 것은
 * 부제(step 표기)와 aria 라벨뿐입니다.
 */
export function TrendsGrid({
  panels,
  section,
  range,
  referenceIso,
  labelPrefix,
  withStep = false,
}: {
  panels: TrendPanel[];
  section: Section<unknown> | undefined;
  range: RangeKey;
  referenceIso?: string;
  /** aria 라벨 앞에 붙는 대상 이름. 없으면 패널 제목만 읽습니다. */
  labelPrefix?: string;
  /** Overview만 step을 부제에 함께 표시합니다. */
  withStep?: boolean;
}) {
  return (
    <div className="grid grid--trends">
      {panels.map((p) => (
        <Panel
          key={p.id}
          title={p.title}
          subtitle={`${RANGE_LABEL[range]}${withStep ? ` · step ${stepLabel(p.stepSeconds)}` : ""} · 단위 ${unitSuffix(p.series[0]!.unit)}`}
          section={section}
          referenceIso={referenceIso}
        >
          <LineChart
            series={p.series}
            stepSeconds={p.stepSeconds}
            ariaLabel={
              labelPrefix
                ? `${labelPrefix} ${p.title}`
                : `${p.title} 시계열. ${p.series.map((s) => s.label).join(", ")}`
            }
          />
        </Panel>
      ))}
    </div>
  );
}

function stepLabel(stepSeconds: number) {
  return stepSeconds >= 3600 ? `${stepSeconds / 3600}시간` : `${stepSeconds / 60}분`;
}

/* ── 필터 칩 ─────────────────────────────────────────────────────────────── */

/**
 * 상태 필터. 선택은 URL에 남고, 데이터가 갱신되어도 유지됩니다.
 * (이슈 #15 완료 기준: 갱신 시 사용자 선택이 초기화되지 않을 것)
 */
export function IssueFilter({
  available,
  selected,
  counts,
  onToggle,
  onClear,
}: {
  available: IssueReason[];
  selected: IssueReason[];
  counts: Partial<Record<IssueReason, number>>;
  onToggle: (r: IssueReason) => void;
  onClear: () => void;
}) {
  if (available.length === 0) return null;
  return (
    <div className="chips" role="group" aria-label="상태 필터">
      {available.map((r) => (
        <button
          key={r}
          type="button"
          className="chip"
          aria-pressed={selected.includes(r)}
          onClick={() => onToggle(r)}
        >
          {ISSUE_LABEL[r]}
          {counts[r] !== undefined && <span className="chip__count num">{counts[r]}</span>}
        </button>
      ))}
      {selected.length > 0 && (
        <button type="button" className="linkish" onClick={onClear}>
          필터 해제
        </button>
      )}
    </div>
  );
}

/* ── 사용량 막대 ─────────────────────────────────────────────────────────── */

/**
 * Request/Limit 대비 비율. 100%를 기준선으로 두고 넘으면 상태 색으로 칠합니다.
 * 색만으로 판단하지 않도록 수치를 항상 함께 씁니다.
 */
export function UsageBar({
  ratio,
  label,
  max = 2,
}: {
  ratio: number | null;
  label: string;
  /**
   * 막대가 표현하는 최대 비율. 초과 할당이 가능한 화면(사용량 vs request)은 2가
   * 기본이며 중앙 눈금이 100%입니다. 노드 request/allocatable처럼 1을 넘을 수
   * 없는 비율은 1을 지정해 0–100% 풀스케일로 그립니다. (#14)
   */
  max?: 1 | 2;
}) {
  if (ratio === null) {
    return (
      <span className="usage">
        <span className="usage__text muted">{label} 미설정</span>
      </span>
    );
  }
  const pct = (Math.min(ratio, max) / max) * 100;
  const tone = ratio >= 1 ? "critical" : ratio >= 0.85 ? "warning" : "healthy";
  return (
    <span className="usage" title={`${label} 대비 ${(ratio * 100).toFixed(0)}%`}>
      <span className={`usage__track usage__track--${tone}`}>
        <span className="usage__fill" style={{ width: `${pct}%` }} />
        {max > 1 && <span className="usage__mark" />}
      </span>
      <span className="usage__text num">{(ratio * 100).toFixed(0)}%</span>
    </span>
  );
}

export function UsageCell({ usage }: { usage: ResourceUsage }) {
  return (
    <span className="row" style={{ gap: "var(--space-4)" }}>
      <span className="num muted" style={{ minWidth: 78 }}>
        {num(usage.cpuMilli)}m
      </span>
      <UsageBar ratio={usage.cpuVsRequest} label="CPU Request" />
    </span>
  );
}

/* ── OwnerReference 체인 ─────────────────────────────────────────────────── */

/**
 * Deployment → ReplicaSet → Pod 관계를 그대로 보여줍니다.
 * 롤아웃 중에는 ReplicaSet이 둘 이상 공존하므로 현재 세대를 표시합니다.
 */
export function OwnerChain({ chain }: { chain: OwnerRef[] }) {
  return (
    <ul className="owner-chain">
      {chain.map((o) => (
        <li key={o.uid} className={o.current ? "owner-chain__item owner-chain__item--current" : "owner-chain__item"}>
          <span className="owner-chain__kind">{o.kind}</span>
          <span className="ds-ident">{o.name}</span>
          {o.revision && <span className="muted">rev {o.revision}</span>}
          {o.pods !== undefined && <span className="muted num">Pod {o.pods}</span>}
          {o.current ? <span className="owner-chain__badge">현재 세대</span> : <span className="muted">이전 세대</span>}
          <span className="ds-ident muted owner-chain__uid" title={o.uid}>
            {o.uid}
          </span>
        </li>
      ))}
    </ul>
  );
}
